const mapOrderToCart = require('@/libraries/DigiTickets/Mappers/mapOrderToCart');
const OnlineTaskType = require('@/lib/OnlineQueue/OnlineTaskType');
const Order = require('@/lib/Orders/Order');
const StashedCart = require('@/lib/StashedCarts/StashedCart');
const { apiResponseToError } = require('@/lib/Api');

/**
 * @constructor
 *
 * @param {Hydrator} hydrator
 * @param {KitchenOrderFactory} kitchenOrderFactory
 * @param {OnlineQueue} onlineQueue
 * @param orderResource
 */
const OrderService = function (
    hydrator,
    kitchenOrderFactory,
    onlineQueue,
    orderResource
) {
    this.hydrator = hydrator;
    this.kitchenOrderFactory = kitchenOrderFactory;
    this.onlineQueue = onlineQueue;
    this.orderResource = orderResource;
};

OrderService.prototype = {
    /**
     * @param {number} orderID
     * @param {Payment[]|PaymentForApiStructure[]} payments
     *
     * @returns {Promise<OnlineTask>}
     */
    addPayments(orderID, payments) {
        return this.onlineQueue.addTask(
            this.onlineQueue.createTask(
                OnlineTaskType.ADD_PAYMENTS,
                {
                    orderID,
                    payments,
                },
                `Add ${payments.length} payment(s) to order ${orderID}.`
            )
        );
    },

    /**
     * @param {number} orderID
     *
     * @returns {Promise<OnlineTask>}
     */
    confirmOrder(orderID) {
        return this.onlineQueue.addTask(
            this.onlineQueue.createTask(
                OnlineTaskType.CONFIRM_ORDER,
                {
                    orderID,
                },
                `Confirm order ${orderID}.`,
            )
        );
    },

    /**
     * Returns all orders that have one or more fulfillments that are still unfulfilled.
     *
     * @return {Promise<Order[]>}
     */
    findUnfulfilledOrders() {
        return new Promise((resolve, reject) => {
            this.orderResource.queryUnfulfilled(
                (response) => {
                    const orders = this.hydrator.hydrateArray(
                        response,
                        () => new Order()
                    );
                    resolve(orders);
                },
                (err) => reject(apiResponseToError(err))
            );
        });
    },

    /**
     * Returns data about food orders that are in progress i.e. the customer is either sitting at a table having a meal
     * or they have gone to the till to pay at the end.
     *
     * This returns all orders that either:
     * - Have one or more fulfillments that are unfilfilled
     * - Are food orders, and are still unpaid
     *
     * Therefore the order contains food items that either required fulfillment or still require fulfillment. If all the
     * items in a order have already been fulfilled, the order should still be returned here until the balance has been
     * settled (for example the customer orders main course and drinks which are fulfilled and later they order some
     * more drinks or a pudding).
     *
     * Once an order has been paid and fulfilled it is complete and will not appear here.
     *
     * @return {Promise<StashedCart[]>}
     */
    findActiveFulfillmentOrders() {
        return new Promise((resolve, reject) => {
            this.orderResource.queryActiveFulfillments(
                (response) => {
                    const orders = this.hydrator.hydrateArray(
                        response,
                        () => new Order()
                    );
                    resolve(this.mapKitchenOrdersToStashedCarts(orders));
                },
                (err) => reject(apiResponseToError(err))
            );
        });
    },

    /**
     * Process an array of orders into an StashedCarts.
     *
     * @param {Order[]} orders
     *
     * @returns {StashedCart[]}
     */
    async mapKitchenOrdersToStashedCarts(orders) {
        return Promise.all(orders.map(async (order) => {
            let stashedCart = new StashedCart();

            let cart = await mapOrderToCart(order);

            // We compare the before and after KitchenOrder to generate the kitchen receipt diff.
            // TODO: Should this be moved to mapOrderToCart?
            cart.restoredKitchenOrder = await this.kitchenOrderFactory.map(order);

            stashedCart.currentCart = cart;
            stashedCart.deviceID = order.deviceID;
            stashedCart.username = order.seller ? order.seller.username : null;
            stashedCart.stashedAt = order.date;

            return stashedCart;
        }));
    },
};

module.exports = OrderService;
